import { sprintf } from 'sprintf-js';
import microTime from "./microTime";
import Config from "./Config";

export default class AnimationHandler
{
    private config: Config;

    private loading: boolean = true;
    private images: Array<HTMLImageElement>;

    private lastImage: HTMLImageElement = null;
    private currentImage: number = 0;

    private lastChange: number = 0;
    private lastComplete: number = 0;

    private forward: boolean = true;

    constructor(image: HTMLImageElement, callback: CallableFunction = null) {
        this.config = Config.fromImage(image);

        image.style.display = 'none';

        const container = document.createElement('figure');
        container.classList.add('animated-image');

        image.parentNode.insertBefore(container, image);

        const mainImage = image;

        const firstImage = new Image();

        firstImage.addEventListener('load', () => {
            this.loadFiles()
                .then((images:Array<HTMLImageElement>) => {
                    container.innerHTML = '';

                    images.forEach((image: HTMLImageElement) => {
                        image.style.display = 'none';

                        mainImage.classList.forEach(className => {
                            image.classList.add(className)
                        })

                        container.appendChild(image)
                    })

                    this.images = images;

                    this.setLoading(false);

                    if (callback) {
                        callback()
                    }

                    this.run();
                })
                .catch(error => {
                    alert(error.message)
                })
        })

        firstImage.src = image.src;

        container.appendChild(firstImage);
        image.parentNode.removeChild(image);
    }

    static register(callback: CallableFunction = null) {
        const baseImages: NodeListOf<HTMLImageElement> = document.querySelectorAll('img.animated');

        baseImages.forEach(image => {
            new AnimationHandler(image, callback)
        })
    }

    run() {
        const now = microTime();

        if (this.lastComplete > 0 && now - this.lastComplete < this.config.replayDelay) {
            return requestAnimationFrame(() => this.run())
        }

        if (this.lastChange > 0 && now - this.lastChange < this.config.delay) {
            return requestAnimationFrame(() => this.run())
        }

        const currentImage = this.images[this.currentImage];

        this.lastChange = now;

        if (null !== this.lastImage) {
            this.lastImage.style.display = 'none';
        }

        currentImage.style.display = 'block';

        this.currentImage = this.forward ? this.currentImage + 1 : this.currentImage - 1;

        if (this.currentImage === this.images.length) {
            if (this.config.bounce) {
                this.forward = false;
                this.currentImage -= 2;
            } else {
                this.lastComplete = now;
                this.currentImage = 0;
            }
        }

        if (this.currentImage < 0) {
            this.forward = true;
            this.lastComplete = now;
            this.currentImage = 0;
        }

        this.lastImage = currentImage;

        requestAnimationFrame(() => this.run())
    }

    loadFiles(): Promise<Array<HTMLImageElement>> {
        this.setLoading(true);

        const first: number = this.config.range[0];
        const last: number = this.config.range[1];

        // @ts-ignore
        return new Promise((resolve, reject) => {
            const promises = [];

            for (let num: number = first; num <= last; num ++) {
                const filename = sprintf(this.config.filenames, num);

                promises.push(this.loadFile(filename))
            }

            // @ts-ignore
            Promise.all(promises)
                .then(images => {
                    resolve(images);
                })
                .catch((path: string) => {
                    reject(new Error('error loading image ' + path))
                })
        })
    }

    loadFile(filename: string): Promise<HTMLImageElement> {
        // @ts-ignore
        return new Promise<any>((resolve, reject) => {
            const path = (this.config.directory + '/' + filename);

            const image = new Image();

            image.addEventListener('load', () => {
                resolve(image)
            })

            image.addEventListener('error', () => {
                reject(path)
            })

            image.src = path;
        });
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
    }
}
